import useGuestSecurity from '../hooks/useGuestSecurity';
import TurnstileChallenge from '../components/auth/TurnstileChallenge';
import React from 'react';
import useSessionStore from '../state/session';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { getSupabaseClient } from '../data/supabase';
import { createGuestAuthClient } from '../data/auth';
import type { SupabaseClient } from '@supabase/supabase-js';
import { apiFetch } from '../api/http';

type Step = 'loading' | 'set-password' | 'accepting' | 'done' | 'error';

const INVITE_ACTIVATION_ERROR = 'This invitation could not be activated. It may be invalid or expired.';

const AcceptInvitePage: React.FC = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') ?? '';
  const email = params.get('email') ?? '';

  const [step, setStep] = React.useState<Step>('loading');
  const [password, setPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [errorMsg, setErrorMsg] = React.useState('');
  const security = useGuestSecurity();
  const [captchaToken, setCaptchaToken] = React.useState('');
  const [captchaReset, setCaptchaReset] = React.useState(0);
  const [role, setRole] = React.useState<string | null>(null);

  const supabase = React.useMemo(() => getSupabaseClient(), []);

  React.useEffect(() => {
    if (!token || !email) {
      setErrorMsg('Invalid invitation link. Missing token or email.');
      setStep('error');
      return;
    }
    (async () => {
      const validationResponse = await apiFetch(
        '/auth/validate-invitation',
        {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token, email }),
        },
        { authMode: 'public' },
      );
      const validationPayload = await validationResponse.json().catch(() => ({}));
      if (!validationResponse.ok) {
        setErrorMsg(
          validationPayload?.error === 'client_address_not_allowed_for_staff_invitation'
            ? 'This address belongs to a client contact and cannot be used for a staff invitation.'
            : 'This invitation is invalid, expired, or was not sent by an authorized administrator.',
        );
        setStep('error');
        return;
      }
      const validatedInvitationRole = typeof validationPayload?.role === 'string'
        ? validationPayload.role
        : null;
      setRole(validatedInvitationRole);
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        setStep('accepting');
        await acceptInvitation(validatedInvitationRole);
      } else {
        setStep('set-password');
      }
    })().catch(() => {
      setErrorMsg('Invitation verification is unavailable. No account or email was created.');
      setStep('error');
    });
  }, []);

  async function acceptInvitation(validatedInvitationRole: string | null, client: SupabaseClient = supabase) {
    setStep('accepting');
    try {
      const guestInvitation = ['guest', 'freelancer'].includes(validatedInvitationRole || '');
      let result = await client.rpc(guestInvitation ? 'accept_guest_invitation' : 'accept_invitation', { invite_token: token });
      if (guestInvitation && !result.error && result.data !== 'ACCEPTED') {
        const { data: current } = await client.auth.getSession();
        await apiFetch('/auth/prepare-guest-invitation', { method:'POST', headers:{ Authorization:'Bearer '+(current.session?.access_token || '') }, body:JSON.stringify({ token,email }) }, { authMode:'public' });
        // A principal remount may have completed this same acceptance while
        // preparation was in flight; the RPC is idempotent for its exact owner.
        result = await client.rpc('accept_guest_invitation', { invite_token: token });
      }
      const { data, error } = result;
      if (error) {
        setErrorMsg(INVITE_ACTIVATION_ERROR);
        setStep('error');
        return;
      }
      if (data !== 'ACCEPTED') {
        setErrorMsg('This invitation has expired or is no longer valid.');
        setStep('error');
        return;
      }

      // New guest activation is accepted before promotion to the shared
      // principal, so a remount cannot revalidate an already-consumed link.
      if (client !== supabase) return true;
      if (guestInvitation) await useSessionStore.getState().loadContext(supabase);
      setRole(validatedInvitationRole);
      setStep('done');

      setTimeout(() => {
        if (guestInvitation) {
          navigate('/account/security', { replace: true });
        } else {
          navigate('/', { replace: true });
        }
      }, 2000);
    } catch {
      setErrorMsg(INVITE_ACTIVATION_ERROR);
      setStep('error');
    }
  }

  async function handleSignUp(e: React.FormEvent) {
    e.preventDefault();
    const minimumLength = ['guest', 'freelancer'].includes(role || '') ? 15 : 6;
    if (password.length < minimumLength) {
      setErrorMsg(`Password must be at least ${minimumLength} characters.`);
      return;
    }
    if (password !== confirmPassword) {
      setErrorMsg('Passwords do not match.');
      return;
    }
    setErrorMsg('');
    setStep('accepting');

    try {
      if (['guest','freelancer'].includes(role || '')) {
        if (!security.data || security.isError) throw new Error('Guest settings unavailable');
        if (security.data.enabled && !captchaToken) { setErrorMsg('Complete the security check before creating your account.'); setStep('set-password'); return; }
        const response = await apiFetch('/auth/activate-guest-invitation',{ method:'POST',headers:{ 'Content-Type':'application/json' },body:JSON.stringify({ token,email,password,captcha_token:captchaToken }) },{ authMode:'public' });
        const payload = await response.json();
        if (!response.ok) {
          if (payload.sign_in_required) { navigate('/login',{ state:{ from: '/accept-invite?'+params.toString() } }); return; }
          setErrorMsg(payload.message || 'Your account could not be activated. Try again.'); setStep('set-password'); return;
        }
        const guestClient = createGuestAuthClient();
        const { error } = await guestClient.auth.verifyOtp({ token_hash:payload.token_hash,type:'magiclink' }); if (error) throw error;
        if (!await acceptInvitation(role, guestClient)) return;
        const { data: activated } = await guestClient.auth.getSession();
        if (!activated.session) throw new Error('Guest activation did not finish');
        const { error: promotionError } = await supabase.auth.setSession({ access_token: activated.session.access_token, refresh_token: activated.session.refresh_token });
        if (promotionError) throw promotionError;
        navigate('/account/security', { replace: true });
        return;
      } else {
      const { error: signUpErr } = await supabase.auth.signUp({ email, password });
      if (signUpErr) {
        const signUpCode = String(signUpErr.code ?? '').toLowerCase();
        const accountAlreadyExists = signUpCode === 'user_already_exists';
        if (accountAlreadyExists) {
          const { error: signInErr } = await supabase.auth.signInWithPassword({ email, password });
          if (signInErr) {
            setErrorMsg('This account already exists, but the password could not be verified. Go to Login or reset your password.');
            setStep('set-password');
            return;
          }
        } else {
          setErrorMsg('Your account could not be created. Verify the invitation and try again.');
          setStep('set-password');
          return;
        }
      }

      }
      await acceptInvitation(role);
    } catch {
      setErrorMsg('Your account could not be created. Verify the invitation and try again.');
      setStep('set-password');
    } finally { setCaptchaToken(''); setCaptchaReset(v => v + 1); }
  }

  return (
    <div style={{ maxWidth: 440, margin: '60px auto', padding: '0 20px' }}>
      <div className="card pad-lg" style={{ textAlign: 'center' }}>
        <h1 style={{ fontSize: '1.5rem', marginBottom: 8 }}>Accept Invitation</h1>

        {step === 'loading' && (
          <p className="fg-muted text-sm">Verifying invitation...</p>
        )}

        {step === 'set-password' && (
          <>
            <p className="text-sm fg-muted" style={{ marginBottom: 20 }}>
              Create your account for <strong>{email}</strong>
            </p>
            {errorMsg && (
              <div className="alert alert-warn" style={{ marginBottom: 12, textAlign: 'left' }}>
                {errorMsg}
              </div>
            )}
            <form noValidate onSubmit={handleSignUp}>
              <div style={{ marginBottom: 12 }}>
                <label className="text-sm" htmlFor="invite-password" style={{ display: 'block', marginBottom: 4, textAlign: 'left' }}>
                  Password
                </label>
                <input
                  id="invite-password"
                  type="password"
                  className="input"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder={['guest','freelancer'].includes(role || '') ? 'At least 15 characters' : 'At least 6 characters'}
                  autoFocus
                  required
                  minLength={['guest','freelancer'].includes(role || '') ? 15 : 6}
                  style={{ width: '100%' }}
                />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label className="text-sm" htmlFor="invite-confirm" style={{ display: 'block', marginBottom: 4, textAlign: 'left' }}>
                  Confirm Password
                </label>
                <input
                  id="invite-confirm"
                  type="password"
                  className="input"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  placeholder="Confirm password"
                  required
                  style={{ width: '100%' }}
                />
              </div>
              {['guest','freelancer'].includes(role || '') && security.data?.enabled && <TurnstileChallenge siteKey={security.data.site_key} onToken={setCaptchaToken} resetKey={captchaReset} />}
              {security.isError && <p role="alert">Guest settings could not be loaded. <button type="button" className="btn" onClick={() => void security.refetch()}>Retry</button></p>}
              <button type="submit" className="btn" style={{ width: '100%' }}>
                Create Account
              </button>
            </form>
          </>
        )}

        {step === 'accepting' && (
          <p className="fg-muted text-sm">Activating your account...</p>
        )}

        {step === 'done' && (
          <>
            <p style={{ color: 'var(--success, #27ae60)', fontWeight: 600 }}>
              Account activated!
            </p>
            <p className="text-sm fg-muted">
              Redirecting to {['guest','freelancer'].includes(role || '') ? 'your account security settings' : 'the dashboard'}...
            </p>
          </>
        )}

        {step === 'error' && (
          <div>
            <p style={{ color: 'var(--danger, #e74c3c)', marginBottom: 12 }}>{errorMsg}</p>
            <button type="button" className="btn sm" onClick={() => navigate('/login', { replace: true })}>
              Go to Login
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AcceptInvitePage;
